<template>
  <div class="container document-view">
    <div class="breadcrumb-voucher py-3">
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <router-link to="/">
              <Icon color="info" name="house" />
            </router-link>
          </li>
          <li class="breadcrumb-item">
            <router-link :to="`/`" class="color--info">
              <span>Emissão de certificado</span>
            </router-link>
          </li>
          <li class="breadcrumb-item active">
            <router-link to="/" class="color--primary disabled fw-600">
              Envio de arquivos
            </router-link>
          </li>
        </ol>
      </nav>
    </div>
    <h2 class="fw-600">Agora vamos precisar de alguns documentos</h2>
    <div class="main gap-4" role="main">
      <div class="d-flex flex-column col-md-12 col-lg-6">
        <p>Você solicitou as alterações abaixo:</p>
        <ul class="list-unstyled">
          <li v-for="(docNote, index) in filteredDocsPf" :key="index">
            <font-awesome-icon
              :icon="['far', 'circle-check']"
              class="me-2 text-success"
            />
            {{ docNote.title }};
          </li>
        </ul>
        <p>
          Para validar essas alterações, verifique o que você vai precisar
          enviar.
        </p>
        <div class="doc-notes py-4 pt-0 px-3 rounded-3">
          <div
            v-for="(docNote, index) in filteredDocsPf"
            :key="index"
            class="pt-5"
          >
            <p>
              <span class="fw-bold">{{ docNote.title }}:</span>
              <br />
              <span v-if="docNote.notes.length > 1" class="fs-7">
                escolha uma das opções abaixo
              </span>
            </p>
            <hr />
            <ul class="mb-0">
              <li
                v-for="(note, index2) in docNote.notes"
                :key="index2"
                class="text-break mt-3"
              >
                <span class="fw-bold">{{ note.title }}: </span
                ><span v-html="note.description"></span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="uploads w-100">
        <Upload
          class="element-sticky"
          :is-mobile="isMobile"
          :files-loaded="filesLoaded"
          :extension-types="extensionsAllows"
          @add-file="handleAddFile"
        />
        <FilesLoader
          :files-loaded="filesLoaded"
          :extension-types="extensionsAllows"
          @handle-delete-file="handleDeleteFile"
        />
      </div>
    </div>
    <div class="d-flex justify-content-end action-section flex-row">
      <button
        class="btn"
        type="button"
        :class="isInvalid ? 'btn-secondary disabled' : 'btn-primary'"
        :disabled="isInvalid"
        @click="sendFiles"
      >
        <span class="me-2">Avançar</span>
        <Icon name="arrow-right" />
      </button>
    </div>
    <Contact style="margin-bottom: 6rem" />
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import Icon from '@/components/Atoms/Icon.vue'
  import Contact from '@/components/Molecules/Contact.vue'
  import Upload from '@/components/Molecules/Upload.vue'
  import { typesCompany } from '@/store/JSON/documentsJson'
  import mobileAndTabletCheck from '@/utils/browserDeviceCheck'
  import { mapActions, mapState } from 'pinia'
  import { useToastGlobal } from '@/store/Pinia/useToastGlobal.store'
  import sentFilesController from '@/infra/sisAr/sentFiles.controller'
  import { useSolicitation } from '@/store/Pinia/useSolicitation.store'
  import { useModalLoading } from '@/store/Pinia/useModalLoading.store'
  import FilesLoader from '@/components/Molecules/FilesLoader.vue'
  import { useDynamicForms } from '@/store/Pinia/useDynamicForms.store'
  import { useOeRecaptcha } from '@/utils/useOeRecaptcha'

  interface INotesPF {
    title: string
    description: string
  }

  interface IObjectDocument {
    key: string
    title: string
    notes: INotesPF[]
  }

  const ChangesPFType = {
    CEI: 'includeCEI',
    PIS: 'includePIS',
  }

  const documentsListPF = [
    {
      key: ChangesPFType.CEI,
      title: 'Inclusão de CEI',
      notes: [
        {
          title: 'CAEPF, CNO e CEI',
          // eslint-disable-next-line
          description: `Espelho do respectivo documento. Link: <a target="_blank" href="http://ceiweb.receita.fazenda.gov.br/ceiweb/modificarSenhaIdentificador.view">http://ceiweb.receita.fazenda.gov.br/ceiweb/modificarSenhaIdentificador.view</a>`,
        },
        {
          title: 'CEI com Espólio',
          description: 'Espelho do CEI PF com Espólio.',
        },
      ],
    },
    {
      key: ChangesPFType.PIS,
      title: 'Inclusão de PIS',
      notes: [
        {
          title: 'PIS',
          // eslint-disable-next-line
          description: `Espelho do PIS e CTPS (modelo atual). Link: <a target="_blank" href="https://consultacadastral.inss.gov.br/Esocial/pages/qualificacao/qualificar.xhtml">https://consultacadastral.inss.gov.br/Esocial/pages/qualificacao/qualificar.xhtml</a>`,
        },
      ],
    },
  ] as IObjectDocument[]

  export default defineComponent({
    name: 'DocumentsUpload',
    components: {
      Icon,
      Contact,
      Upload,
      FilesLoader,
    },
    setup() {
      return {
        oeRecaptcha: useOeRecaptcha(),
      }
    },
    data: () => ({
      form: {
        types: typesCompany,
        documents: [] as Array<IObjectDocument>,
      },
      isMobile: false,
      filesLoaded: [] as Array<File>,
      extensionsAllows: ['png', 'jpg', 'jpeg', 'doc', 'docs', 'docx', 'pdf'],
      changesPF: [] as Array<string>,
    }),
    computed: {
      ...mapState(useSolicitation, ['getSolicitation']),
      ...mapState(useDynamicForms, ['formData']),
      isInvalid() {
        return this.filesLoaded.length === 0 || this.findDocumentError()
      },
      filteredDocsPf() {
        return documentsListPF.filter((docNote) =>
          this.changesPF.includes(docNote.key),
        )
      },
    },
    methods: {
      ...mapActions(useToastGlobal, ['toggleToastGlobal']),
      ...mapActions(useModalLoading, ['setIsLoading']),
      handleAddFile(newFiles: File[]) {
        if (this.filesLoaded.length >= 10) {
          return
        }
        if (this.filesLoaded.length > 0) {
          this.filesLoaded = [...this.filesLoaded, ...newFiles]
        } else {
          this.filesLoaded = [...newFiles]
        }
      },
      handleDeleteFile(indexFile: number) {
        const filtered = this.filesLoaded.filter(
          (x: File, i: number) => i !== indexFile,
        )
        this.filesLoaded = filtered
      },
      async sendFiles() {
        try {
          this.setIsLoading({
            isLoading: true,
            title: 'Aguarde enquanto enviamos seus documentos',
            message: '',
            image: 'fileUpload',
          })

          await sentFilesController.sendFiles(
            this.filesLoaded,
            this.oeRecaptcha.getToken,
          )

          this.$router.push('/success-upload-files')
        } catch (error) {
          this.toggleToastGlobal({
            isShow: true,
            message:
              'Houve um erro ao enviar seus documentos, tente novamente mais tarde',
          })
          this.setIsLoading({ isLoading: false })
        } finally {
          this.setIsLoading({ isLoading: false })
        }
      },
      findDocumentError() {
        return !!this.filesLoaded.find(
          (x: File) => !this.extensionsAllows.includes(x.type.split('/')[1]),
        )
      },
    },
    mounted() {
      this.oeRecaptcha.showBadge()

      this.isMobile = mobileAndTabletCheck()
      if (this.formData.cei !== '') {
        this.changesPF.push(ChangesPFType.CEI)
      }
      if (this.formData.pis !== '') {
        this.changesPF.push(ChangesPFType.PIS)
      }
    },
    beforeRouteEnter(to, from, next) {
      const solicitationStore = useSolicitation()

      if (!solicitationStore.getSolicitation.cdSolicitacao) {
        return next('/')
      }
      return next()
    },
    beforeRouteLeave() {
      this.oeRecaptcha.hideBadge()
    },
  })
</script>

<style lang="css" scoped>
  .doc-notes {
    background-color: #f0f0f3;
  }

  .text-documents-required {
    display: flex;
    flex-direction: column;
    margin-top: 1rem;
    padding: 1rem;
    border-radius: 2rem;
    background-color: var(--color-grayscale-light);
  }
  .document-view {
    padding: 1rem;
    text-align: left !important;
  }

  .element-sticky {
    position: -webkit-sticky; /* Safari */
    position: sticky;
    top: 1rem;
  }
  .main {
    position: relative;
    margin-top: 2.5rem;
    display: flex;
    margin-bottom: 1rem;
  }

  @media screen and (max-width: 990px) {
    .main {
      flex-direction: column;
    }
    .action-section > button {
      width: 100%;
    }
  }
</style>
